import { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Link,
  ListItem,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tag,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { Panel, PanelHeader, PanelHeading } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { i18n } from '@investigator/shared/localization';
import { FeedbackThumbs } from '@investigator/shared/components/CustomerFeedback';
import { FeedbackEvent } from '@investigator/shared/types';
import { useFeedbackForm, useParseBackticks } from '@investigator/shared/hooks';
import formatUnixTimeStampAsDisplayString from '@investigator/shared/utils/formatUnixTimeStampAsDisplayString';
import {
  useEntitiesContext,
  useEntityAgenticTriageReportContext,
} from '../../context';
import { useParseAnalysisFindings, useProcessArtifacts } from '../../hooks';
import { mapVerdictToAIInsightVerdict } from '../../utils';
import { AiVerdictBadge } from '../AiVerdictBadge';
import { ChatGPTIcon } from '../ChatGPTIcon';

interface AIAnalysisArtifact {
  id: string;
  label: string;
  title: string;
  description: string;
  evidenceLink: string;
}

interface AIAnalysisFinding {
  id: string;
  /** Text segments and artifact references in order */
  content: Array<string | { artifactId: string }>;
}

interface ArtifactPopoverProps {
  artifact: AIAnalysisArtifact;
}

/**
 * Renders an artifact as a grey tag with a popover on hover
 */
const ArtifactPopover = ({ artifact }: ArtifactPopoverProps): JSX.Element => {
  const modalCopy = i18n.pages.entities.entityAnalysisModal;

  return (
    <Popover trigger="hover" placement="top" openDelay={200}>
      <PopoverTrigger>
        <Tag
          size="sm"
          bg="gray.600"
          color="white"
          cursor="pointer"
          verticalAlign="baseline"
        >
          {artifact.label}
        </Tag>
      </PopoverTrigger>
      <PopoverContent w="300px">
        <PopoverArrow />
        <PopoverBody>
          <VStack align="start" spacing={2}>
            <Text fontWeight="bold" textStyle="body-md">
              {artifact.title}
            </Text>
            <Text textStyle="body-sm" color="text.secondary">
              {artifact.description}
            </Text>
            <Link
              href={artifact.evidenceLink}
              color="blue.400"
              textStyle="body-sm"
              display="flex"
              alignItems="center"
              gap={1}
            >
              {modalCopy.viewEvidence} →
            </Link>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

interface FindingsListProps {
  findings: AIAnalysisFinding[];
  artifacts: AIAnalysisArtifact[];
}

/**
 * Renders the bulleted list of findings with embedded artifact popovers
 */
const FindingsList = ({
  findings,
  artifacts,
}: FindingsListProps): JSX.Element => {
  const { parseBackticks } = useParseBackticks();

  const artifactMap = useMemo(() => {
    const map = new Map<string, AIAnalysisArtifact>();
    artifacts.forEach((artifact) => map.set(artifact.id, artifact));
    return map;
  }, [artifacts]);

  return (
    <UnorderedList spacing={3} pl={2} color="text.primary">
      {findings.map((finding) => (
        <ListItem key={finding.id} textStyle="body-md">
          {finding.content.map((segment, index) => {
            if (typeof segment === 'string') {
              return <span key={index}>{parseBackticks(segment)}</span>;
            }
            const artifact = artifactMap.get(segment.artifactId);
            if (!artifact) return null;
            return <ArtifactPopover key={index} artifact={artifact} />;
          })}
        </ListItem>
      ))}
    </UnorderedList>
  );
};

export const AIAnalysisPanel = (): JSX.Element | null => {
  const modalCopy = i18n.pages.entities.entityAnalysisModal;
  const { selectedEntity } = useEntitiesContext();
  const { report, isLoading, error } = useEntityAgenticTriageReportContext();
  const { parseBackticks } = useParseBackticks();
  const [isExpanded, setIsExpanded] = useState(false);

  // Get artifacts from analysis_summary (not yet available from backend)
  // TODO: Backend will add artifacts field in the future
  const rawArtifacts: AIAnalysisArtifact[] = [];
  const processedArtifacts = useProcessArtifacts(rawArtifacts);

  const feedbackForm = useFeedbackForm(
    modalCopy.feedbackForm,
    FeedbackEvent.aiAnalysisFeedback,
  );

  const trackingContext = useMemo(
    () => ({
      feature: 'agentic-triage',
      entityName: selectedEntity?.entityName,
      entityType: selectedEntity?.entityType,
    }),
    [selectedEntity?.entityName, selectedEntity?.entityType],
  );

  // Parse findings from details using the hook
  const { findings, artifacts: parsedArtifacts } = useParseAnalysisFindings({
    details:
      (report?.analysis_summary?.details?.filter(Boolean) as string[]) || [],
    artifacts: processedArtifacts ?? [],
  });

  const analysisData = useMemo(() => {
    if (!report?.analysis_summary) {
      return null; // No analysis summary available
    }

    return {
      verdict: report.analysis_summary.verdict
        ? mapVerdictToAIInsightVerdict(report.analysis_summary.verdict)
        : undefined,
      shortDescription: report.analysis_summary.description,
      findings,
      artifacts: parsedArtifacts,
    };
  }, [report?.analysis_summary, findings, parsedArtifacts]);

  // Use created_at from the report for "Last Updated" to be consistent with entity table
  const lastUpdatedDisplay = report?.created_at
    ? modalCopy.lastUpdated.replace(
        '{0}',
        formatUnixTimeStampAsDisplayString(report.created_at),
      )
    : '';

  const toggleText = isExpanded ? modalCopy.less : modalCopy.more;

  // Handle error state - hide panel gracefully
  if (error) {
    return null;
  }

  // Show panel with loading state while data is being fetched
  if (isLoading) {
    return (
      <Panel w="100%">
        <PanelHeader pb={0}>
          <Flex justify="space-between" align="center" w="100%">
            <PanelHeading>
              <HStack spacing={2}>
                <ChatGPTIcon boxSize={6} />
                <Text>{modalCopy.aiAnalysis}</Text>
              </HStack>
            </PanelHeading>
          </Flex>
        </PanelHeader>
        <Box mt={4}>
          <GraphQLReqStatus loading={true} />
        </Box>
      </Panel>
    );
  }

  // Show informational message if report exists but analysis_summary is missing
  if (report && !analysisData) {
    return (
      <Panel w="100%">
        <PanelHeader pb={0}>
          <Flex justify="space-between" align="center" w="100%">
            <PanelHeading>
              <HStack spacing={2}>
                <ChatGPTIcon boxSize={6} />
                <Text>{modalCopy.aiAnalysis}</Text>
              </HStack>
            </PanelHeading>
          </Flex>
        </PanelHeader>
        <Box>
          <Text color="text.secondary" textStyle="body-md">
            {modalCopy.analysisUnavailable}
          </Text>
        </Box>
      </Panel>
    );
  }

  // Don't render panel if no report at all
  if (!report) {
    return null;
  }

  // Render panel with analysis data
  return (
    <Panel w="100%">
      <PanelHeader pb={0}>
        <Flex justify="space-between" align="center" w="100%">
          <PanelHeading>
            <HStack spacing={2}>
              <ChatGPTIcon boxSize={6} />
              <Text>{modalCopy.aiAnalysis}</Text>
              {lastUpdatedDisplay && (
                <Text color="gray.400" fontWeight="normal">
                  {lastUpdatedDisplay}
                </Text>
              )}
            </HStack>
          </PanelHeading>
          <FeedbackThumbs
            config={feedbackForm}
            trackingContext={trackingContext}
          />
        </Flex>
      </PanelHeader>
      <Box>
        {/* Verdict Badge - Always visible */}
        {analysisData?.verdict && (
          <Box mb={3}>
            <AiVerdictBadge verdict={analysisData.verdict} />
          </Box>
        )}

        {/* Content Section - Toggles between description and findings */}
        {isExpanded ? (
          <FindingsList
            findings={analysisData?.findings || []}
            artifacts={analysisData?.artifacts || []}
          />
        ) : (
          <Text color="text.primary" textStyle="body-md">
            {parseBackticks(analysisData?.shortDescription || '')}
          </Text>
        )}

        {/* Toggle Link */}
        <Box mt={3}>
          <Link
            color="blue.400"
            textStyle="body-md"
            onClick={() => setIsExpanded(!isExpanded)}
            cursor="pointer"
          >
            {toggleText}
          </Link>
        </Box>
      </Box>
    </Panel>
  );
};
